import {
  Grid,
  Box,
  VStack,
  HStack,
  Button,
  Collapse,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Badge,
  Text
} from '@chakra-ui/react'
import { FC, ReactNode, useEffect, useState, useMemo } from 'react'
import { FiEye, FiCheck, FiX } from 'react-icons/fi'

import useApproval from '@hook/mrp/use-approval'
import useFilters from '@hook/shared/use-filters'
import useLayout from '@hook/shared/use-layout'
import useSessions from '@hook/shared/use-sessions'

import Layout from '@component/Layout'
import LayoutEmpty from '@component/Layout/Empty'
import LayoutFilters from '@component/Layout/Filters'
import LayoutLoader from '@component/Layout/Loader'
import LayoutTools from '@component/Layout/Tools'
import ApproveDialog from '@component/Modules/Mrp/Approval/ApproveDialog'
import GroupHeader from '@component/Modules/Mrp/Approval/GroupHeader'
import GroupTable from '@component/Modules/Mrp/Approval/GroupTable'
import VisualizarModal from '@component/Modules/Mrp/Approval/VisualizarModal'

import { formatFornecedor, getItensComFalha } from '@helper/mrp/approval'

import {
  IApprovalFormData,
  IPendingOrderGroup,
  ApprovalTabType
} from '@base/@types/providers/Mrp/ApprovalProvider'

interface GroupListProps {
  groups: IPendingOrderGroup[]
  emptyTitle: string
  emptyMessage: string
  notice?: string
  expandedGroups: Set<string>
  onToggleExpand: (groupId: string) => void
  onVisualizar: (group: IPendingOrderGroup) => void
  onApprove?: (group: IPendingOrderGroup, aprovar: boolean) => void
  pagination: ReactNode
}

// Lista de pedidos agrupados (mesma estrutura nas abas Pendentes/Aprovados/Rejeitados)
const GroupList: FC<GroupListProps> = ({
  groups,
  emptyTitle,
  emptyMessage,
  notice,
  expandedGroups,
  onToggleExpand,
  onVisualizar,
  onApprove,
  pagination
}) => (
  <>
    {notice && (
      <Text fontSize="sm" color="gray.600" mb={4}>
        {notice}
      </Text>
    )}
    {groups.length === 0 ? (
      <LayoutEmpty title={emptyTitle} message={emptyMessage} />
    ) : (
      <Box maxH="70vh" overflowY="auto" pr={2}>
        <VStack spacing={4} align="stretch">
          {groups.map(group => {
            const isExpanded = expandedGroups.has(group.idRequestSap)

            return (
              <Box key={group.idRequestSap}>
                <GroupHeader
                  group={group}
                  isExpanded={isExpanded}
                  onToggleExpand={() => onToggleExpand(group.idRequestSap)}
                />

                <Collapse in={isExpanded} animateOpacity>
                  <Box
                    bg="white"
                    borderWidth="0 1px 1px 1px"
                    borderColor="gray.200"
                    p={4}
                  >
                    <HStack justify="flex-end" mb={4}>
                      <Button
                        size="sm"
                        leftIcon={<FiEye />}
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => onVisualizar(group)}
                      >
                        Visualizar
                      </Button>

                      {onApprove && (
                        <>
                          <Button
                            size="sm"
                            leftIcon={<FiCheck />}
                            colorScheme="green"
                            onClick={() => onApprove(group, true)}
                          >
                            Aprovar
                          </Button>

                          <Button
                            size="sm"
                            leftIcon={<FiX />}
                            colorScheme="red"
                            variant="outline"
                            onClick={() => onApprove(group, false)}
                          >
                            Rejeitar
                          </Button>
                        </>
                      )}
                    </HStack>
                    <Divider mb={4} />

                    {/* Tabela de itens */}
                    <GroupTable items={group.items} />
                  </Box>
                </Collapse>
              </Box>
            )
          })}

          {pagination}
        </VStack>
      </Box>
    )}
  </>
)

const ApprovalMrp: FC = () => {
  const { menuToggle } = useLayout()
  const { hasSessions } = useSessions()

  const {
    loading,
    paginatedGroups,
    groupedOrders,
    currentPage,
    totalPages,
    activeTab,
    pendingOrders,
    approvedOrders,
    rejectedOrders,
    fetchOrders,
    approveOrder,
    setCurrentPage,
    setActiveTab,
    filters,
    setFilters,
    filtersList,
    params,
    paramsDates,
    hasActiveFilters,
    handleUpdateParams,
    applyFilters,
    clearAllFilters
  } = useApproval()
  const { getFilters } = useFilters()
  const filteredEmptyMessage =
    'Nenhum pedido corresponde aos filtros selecionados. Ajuste ou limpe os filtros.'

  // Carregar opções dos filtros
  useEffect(() => {
    getFilters({
      centers: true,
      compactCategories: true,
      productManagers: true,
      solicitantes: true
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set())
  const [selectedGroup, setSelectedGroup] = useState<IPendingOrderGroup | null>(
    null
  )
  const [approveDialogOpen, setApproveDialogOpen] = useState(false)
  const [visualizarModalOpen, setVisualizarModalOpen] = useState(false)
  const [approveAction, setApproveAction] = useState<boolean>(true)

  // Carregar dados na inicialização
  useEffect(() => {
    fetchOrders()
  }, [fetchOrders])

  const height = useMemo(
    () =>
      menuToggle
        ? hasSessions
          ? 'calc(100vh - 30px)'
          : '100vh'
        : hasSessions
        ? 'calc(100vh - 160px)'
        : 'calc(100vh - 142px)',
    [menuToggle, hasSessions]
  )

  const selectedItensFalhos = useMemo(
    () => getItensComFalha(selectedGroup?.items),
    [selectedGroup]
  )

  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev)
      if (newSet.has(groupId)) {
        newSet.delete(groupId)
      } else {
        newSet.add(groupId)
      }
      return newSet
    })
  }

  const handleVisualizar = (group: IPendingOrderGroup) => {
    setSelectedGroup(group)
    setVisualizarModalOpen(true)
  }

  const handleApproveFromMenu = (
    group: IPendingOrderGroup,
    aprovar: boolean
  ) => {
    setSelectedGroup(group)
    setApproveAction(aprovar)
    setApproveDialogOpen(true)
  }

  // Modal e diálogo enviam `aprovar` explícito; o resultado é devolvido para
  // que só fechem no sucesso
  const handleApprove = async (data: IApprovalFormData) => {
    if (!selectedGroup) return false

    return approveOrder({
      idRequestSap: selectedGroup.idRequestSap,
      ...data
    })
  }

  const getTabIndex = (tab: ApprovalTabType): number => {
    switch (tab) {
      case 'pendentes':
        return 0
      case 'aprovados':
        return 1
      case 'rejeitados':
        return 2
      default:
        return 0
    }
  }

  const getTabByIndex = (index: number): ApprovalTabType => {
    switch (index) {
      case 0:
        return 'pendentes'
      case 1:
        return 'aprovados'
      case 2:
        return 'rejeitados'
      default:
        return 'pendentes'
    }
  }

  const countGroups = (orders: { idRequestSap: string }[]) =>
    new Set(orders.map(o => o.idRequestSap)).size

  const renderPagination = () => {
    if (totalPages <= 1) return null

    const pages = []
    const maxVisiblePages = 5
    const halfVisible = Math.floor(maxVisiblePages / 2)

    let startPage = Math.max(1, currentPage - halfVisible)
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    return (
      <HStack justify="center" mt={6}>
        <Button
          size="sm"
          onClick={() => setCurrentPage(currentPage - 1)}
          isDisabled={currentPage === 1}
        >
          Anterior
        </Button>

        {pages.map(page => (
          <Button
            key={page}
            size="sm"
            variant={page === currentPage ? 'solid' : 'outline'}
            colorScheme={page === currentPage ? 'blue' : 'gray'}
            onClick={() => setCurrentPage(page)}
          >
            {page}
          </Button>
        ))}

        <Button
          size="sm"
          onClick={() => setCurrentPage(currentPage + 1)}
          isDisabled={currentPage === totalPages}
        >
          Próximo
        </Button>
      </HStack>
    )
  }

  const groupListProps = {
    groups: paginatedGroups,
    expandedGroups,
    onToggleExpand: toggleGroupExpansion,
    onVisualizar: handleVisualizar,
    pagination: renderPagination()
  }

  const sessionNotice = (acao: string) =>
    `Exibindo apenas os pedidos ${acao} nesta sessão${
      hasActiveFilters ? ' que correspondem aos filtros aplicados' : ''
    }. O histórico é perdido ao recarregar a página.`

  return (
    <Layout title="Aprovação de Pedidos MRP">
      <Grid
        w="100vw"
        bg="white"
        h={height}
        templateColumns={!filters ? '100vw' : '260px calc(100vw - 260px)'}
      >
        {filters && (
          <LayoutFilters
            filters={filtersList}
            values={params}
            defaultDate={paramsDates}
            onChange={handleUpdateParams}
            loading={loading}
            canCancel={false}
            submitLabel="Filtrar"
            onFilter={applyFilters}
            onClearAllFilters={clearAllFilters}
          />
        )}
        <Box h={height} maxW="100%" overflow="auto">
          <LayoutTools
            title="Aprovação de Pedidos MRP"
            filters={filters}
            toggleFilters={setFilters.toggle}
            isDisabled={loading}
          />

          {loading ? (
            <LayoutLoader
              title="Carregando pedidos..."
              message="Aguarde enquanto buscamos os pedidos de aprovação"
            />
          ) : (
            <Box p={6}>
              <Tabs
                index={getTabIndex(activeTab)}
                onChange={index => setActiveTab(getTabByIndex(index))}
                variant="enclosed"
                colorScheme="blue"
              >
                <TabList>
                  <Tab>
                    <HStack>
                      <span>Pendentes</span>
                      <Badge colorScheme="orange" fontSize="xs">
                        {activeTab === 'pendentes'
                          ? groupedOrders.length
                          : countGroups(pendingOrders)}
                      </Badge>
                    </HStack>
                  </Tab>
                  <Tab>
                    <HStack>
                      <span>Aprovados nesta sessão</span>
                      <Badge colorScheme="green" fontSize="xs">
                        {activeTab === 'aprovados'
                          ? groupedOrders.length
                          : countGroups(approvedOrders)}
                      </Badge>
                    </HStack>
                  </Tab>
                  <Tab>
                    <HStack>
                      <span>Rejeitados nesta sessão</span>
                      <Badge colorScheme="red" fontSize="xs">
                        {activeTab === 'rejeitados'
                          ? groupedOrders.length
                          : countGroups(rejectedOrders)}
                      </Badge>
                    </HStack>
                  </Tab>
                </TabList>

                <TabPanels>
                  <TabPanel px={0}>
                    <GroupList
                      {...groupListProps}
                      emptyTitle="Nenhum pedido pendente!"
                      emptyMessage={
                        hasActiveFilters
                          ? filteredEmptyMessage
                          : 'Não há pedidos aguardando aprovação no momento'
                      }
                      onApprove={handleApproveFromMenu}
                    />
                  </TabPanel>

                  <TabPanel px={0}>
                    <GroupList
                      {...groupListProps}
                      notice={sessionNotice('aprovados')}
                      emptyTitle="Nenhum pedido aprovado nesta sessão!"
                      emptyMessage={
                        hasActiveFilters
                          ? filteredEmptyMessage
                          : 'Os pedidos aprovados nesta sessão aparecerão aqui'
                      }
                    />
                  </TabPanel>

                  <TabPanel px={0}>
                    <GroupList
                      {...groupListProps}
                      notice={sessionNotice('rejeitados')}
                      emptyTitle="Nenhum pedido rejeitado nesta sessão!"
                      emptyMessage={
                        hasActiveFilters
                          ? filteredEmptyMessage
                          : 'Os pedidos rejeitados nesta sessão aparecerão aqui'
                      }
                    />
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </Box>
          )}
        </Box>
      </Grid>

      {/* Modals */}
      <ApproveDialog
        isOpen={approveDialogOpen}
        onClose={() => setApproveDialogOpen(false)}
        onConfirm={handleApprove}
        pedidoId={selectedGroup?.idRequestSap || ''}
        fornecedor={
          selectedGroup?.fornecedores.map(formatFornecedor).join(', ') || ''
        }
        aprovar={approveAction}
        itensFalhos={selectedItensFalhos}
      />

      <VisualizarModal
        isOpen={visualizarModalOpen}
        onClose={() => setVisualizarModalOpen(false)}
        onApprove={activeTab === 'pendentes' ? handleApprove : undefined}
        group={selectedGroup}
        showApprovalForm={activeTab === 'pendentes'}
      />
    </Layout>
  )
}

export default ApprovalMrp
